import React from 'react';
import { usesNewMeters } from '../../utils/meterCutover';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Svg,
  Path,
} from '@react-pdf/renderer';

// Create styles matching the MTVH Monthly Inspection Report PDF
const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 10,
    lineHeight: 1.3,
    position: 'relative',
  },

  // Cover Page Styles
  coverPage: {
    position: 'relative',
    padding: 0,
    margin: 0,
  },
  coverBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  coverPageInnerView: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    position: 'relative',
  },
  coverLogo: {
    position: 'absolute',
    top: 35,
    right: 45,
    width: 150,
  },
  coverTitleBox: {
    position: 'absolute',
    top: 180,
    left: 30,
    backgroundColor: '#1E3A5F',
    padding: 20,
    paddingRight: 40,
    maxWidth: 280,
  },
  coverTitle: {
    fontSize: 26,
    fontWeight: 'bold',
    color: 'white',
    lineHeight: 1.2,
  },
  coverFooter: {
    position: 'absolute',
    bottom: 50,
    right: 30,
    textAlign: 'right',
    // Semi-transparent background for better text visibility on image (shadow effect)
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 4,
  },
  coverFooterWebsite: {
    color: 'white',
    fontSize: 14,
    marginBottom: 5,
    textAlign: 'right',
    fontWeight: 'normal',
  },
  coverFooterPhone: {
    color: 'white',
    fontSize: 22,
    textAlign: 'right',
    fontWeight: 'bold',
  },

  // Content Page Styles
  contentPage: {
    padding: 30,
    paddingTop: 20,
  },
  logoContainer: {
    alignItems: 'flex-end',
    marginBottom: 20,
  },
  pageLogo: {
    width: 140,
  },
  pageTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1E3A5F',
    marginBottom: 20,
  },

  // Info Tables
  infoTable: {
    marginBottom: 15,
    border: '1 solid #1E3A5F',
  },
  infoRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #1E3A5F',
  },
  infoRowLast: {
    flexDirection: 'row',
  },
  infoLabel: {
    width: '25%',
    backgroundColor: '#1E3A5F',
    color: 'white',
    padding: 8,
    fontSize: 10,
    fontWeight: 'bold',
  },
  infoValue: {
    width: '70%',
    padding: 8,
    fontSize: 10,
    backgroundColor: 'white',
  },

  // Two-column info table
  infoTableTwoCol: {
    marginBottom: 15,
    border: '1 solid #1E3A5F',
  },
  infoRowTwoCol: {
    flexDirection: 'row',
    borderBottom: '1 solid #1E3A5F',
  },
  infoLabelSmall: {
    width: '25%',
    backgroundColor: '#1E3A5F',
    color: 'white',
    padding: 8,
    fontSize: 10,
    fontWeight: 'bold',
  },
  infoValueSmall: {
    width: '25%',
    padding: 8,
    fontSize: 10,
    backgroundColor: 'white',
    borderRight: '1 solid #1E3A5F',
  },
  infoValueSmallLast: {
    width: '25%',
    padding: 8,
    fontSize: 10,
    backgroundColor: 'white',
  },

  // Meter readings table
  meterTable: {
    marginBottom: 20,
    border: '1 solid #1E3A5F',
  },
  meterRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #1E3A5F',
  },
  meterRowLast: {
    flexDirection: 'row',
  },
  meterLabel: {
    width: '25%',
    backgroundColor: '#1E3A5F',
    color: 'white',
    padding: 8,
    fontSize: 9,
    fontWeight: 'bold',
  },
  meterValue: {
    width: '25%',
    padding: 8,
    fontSize: 9,
    backgroundColor: 'white',
    borderRight: '1 solid #1E3A5F',
  },
  meterSerialLabel: {
    width: '25%',
    backgroundColor: '#1E3A5F',
    color: 'white',
    padding: 8,
    fontSize: 9,
    fontWeight: 'bold',
  },
  meterSerialValue: {
    width: '25%',
    padding: 8,
    fontSize: 9,
    backgroundColor: 'white',
  },

  // Photo Section Styles
  photoSection: {
    marginBottom: 10,
  },
  photoRow: {
    flexDirection: 'row',
    height: 105,
  },
  photoLabel: {
    width: 28,
    backgroundColor: '#1E3A5F',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  photoLabelInner: {
    width: 100,
    height: 28,
    justifyContent: 'center',
    alignItems: 'center',
    transform: 'rotate(-90deg)',
  },
  photoLabelText: {
    color: 'white',
    fontSize: 9,
    fontWeight: 'bold',
    textAlign: 'center',
    letterSpacing: 1,
  },
  photoContainer: {
    flex: 1,
    flexDirection: 'row',
    marginLeft: 5,
  },
  photoWrapper: {
    flex: 1,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 5,
  },
  photoWrapperLast: {
    flex: 1,
    backgroundColor: '#f0f0f0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  photo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  noPhotoText: {
    fontSize: 8,
    color: '#999',
  },

  // Meter Photo Grid (2 per row, label bar underneath — matches the standard report)
  meterPhotoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 15,
  },
  meterPhotoBox: {
    width: '49%',
    marginRight: '1%',
    marginBottom: 10,
  },
  meterPhotoBoxRight: {
    width: '49%',
    marginLeft: '1%',
    marginBottom: 10,
  },
  meterPhotoImage: {
    width: '100%',
    height: 150,
    backgroundColor: '#f0f0f0',
    objectFit: 'cover',
  },
  meterPhotoLabel: {
    backgroundColor: '#1E3A5F',
    padding: 8,
  },
  meterPhotoLabelText: {
    color: 'white',
    fontSize: 10,
    fontWeight: 'bold',
  },

  // Checklist Styles
  checklistTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1E3A5F',
    marginBottom: 15,
  },
  checklistTable: {
    border: '1 solid #333',
    marginBottom: 20,
  },
  checklistHeader: {
    flexDirection: 'row',
    backgroundColor: 'white',
    borderBottom: '1 solid #333',
  },
  checklistHeaderCell: {
    padding: 8,
    fontSize: 10,
    fontWeight: 'bold',
    textAlign: 'center',
    borderRight: '1 solid #333',
  },
  checklistHeaderName: {
    width: '20%',
    borderRight: '1 solid #333',
  },
  checklistHeaderCondition: {
    width: '25%',
    borderRight: '1 solid #333',
  },
  checklistHeaderNotes: {
    width: '55%',
  },
  conditionSubHeader: {
    flexDirection: 'row',
  },
  conditionGood: {
    width: '50%',
    padding: 5,
    fontSize: 9,
    textAlign: 'center',
    borderRight: '1 solid #333',
  },
  conditionAttention: {
    width: '50%',
    padding: 5,
    fontSize: 9,
    textAlign: 'center',
  },
  checklistRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #333',
  },
  checklistRowLast: {
    flexDirection: 'row',
  },
  checklistName: {
    width: '20%',
    padding: 8,
    fontSize: 10,
    borderRight: '1 solid #333',
  },
  checklistConditionCell: {
    width: '25%',
    flexDirection: 'row',
    borderRight: '1 solid #333',
  },
  checklistGood: {
    width: '50%',
    padding: 8,
    borderRight: '1 solid #333',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  checklistAttention: {
    width: '50%',
    padding: 8,
    textAlign: 'center',
  },
  checklistNotes: {
    width: '55%',
    padding: 8,
    fontSize: 10,
  },
  checkmark: {
    fontSize: 12,
    color: '#1E3A5F',
  },

  // Conclusion styles
  priorityNote: {
    fontSize: 10,
    fontStyle: 'italic',
    color: '#1E3A5F',
    marginBottom: 20,
    lineHeight: 1.4,
  },
  conclusionTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  conclusionText: {
    fontSize: 10,
    marginBottom: 10,
    lineHeight: 1.4,
  },
  signOff: {
    fontSize: 10,
    marginBottom: 30,
  },
  inspectorInfo: {
    marginTop: 20,
  },
  inspectorName: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 5,
  },
  inspectorEmail: {
    fontSize: 10,
    fontWeight: 'bold',
  },

  // Footer styles
  pageFooter: {
    position: 'absolute',
    bottom: 20,
    right: 30,
    fontSize: 9,
    color: '#666',
  },

  // Footer Page Styles
  footerPage: {
    padding: 30,
    paddingTop: 20,
  },
  footerContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 150,
  },
  footerCompanyName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1E3A5F',
    marginBottom: 15,
    textAlign: 'center',
  },
  footerAddress: {
    fontSize: 11,
    marginBottom: 3,
    textAlign: 'center',
    color: '#333',
  },
  footerEmail: {
    fontSize: 11,
    marginTop: 15,
    textAlign: 'center',
  },
  footerEmailLabel: {
    color: '#1E3A5F',
    fontWeight: 'bold',
  },
  footerWebsite: {
    fontSize: 11,
    color: '#1E3A5F',
    fontWeight: 'bold',
    marginTop: 5,
    textAlign: 'center',
  },
  footerLogosContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 50,
    width: '100%',
    gap: 8,
  },
  footerLogoItem: {
    width: 42,
    height: 28,
    objectFit: 'contain',
  },
});

// Month names for display
const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const MTVHMonthlyInspectionPDF = ({ data }) => {
  if (!data) return null;

  // Get month name
  const getMonthName = (monthNumber) => {
    const month = parseInt(monthNumber);
    return monthNames[month - 1] || 'Unknown';
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // Handle various date formats
    if (dateString.includes('/')) {
      return dateString; // Already in d/m/Y format
    }
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      }
    } catch (e) {
      console.error('Date parsing error:', e);
    }
    return dateString;
  };

  // Get checklist status
  const getChecklistStatus = (section) => {
    if (!data[section] || typeof data[section] !== 'object') {
      return { isGood: true, notes: '' };
    }

    const sectionData = data[section];

    // Check if any item requires attention (condition === '0' means requires attention)
    const requiresAttention = Object.values(sectionData).some(item => {
      if (!item) return false;
      if (typeof item === 'boolean') return !item;
      if (typeof item === 'object') {
        // Check condition field - '0' means requires attention, '1' or undefined means good
        if (item.condition === '0' || item.condition === 0) return true;
        return item.status === 'requires_attention' ||
               item.status === 'bad' ||
               item.status === 'not_good' ||
               item.status === false ||
               item.value === 'bad' ||
               item.value === 'not_good';
      }
      return false;
    });

    // Placeholder values that should be treated as "no response selected"
    const placeholderValues = [
      'choose response',
      'please select',
      'select response',
      'select...',
      'choose...',
      '',
      null,
      undefined
    ];

    // Collect notes from items - show notes regardless of condition (matches Laravel behavior)
    const notes = Object.entries(sectionData)
      .filter(([_, item]) => {
        if (!item) return false;
        if (typeof item !== 'object') return false;

        const noteText = item.notes || item.comment;
        if (!noteText) return false;

        // Filter out placeholder values in notes themselves (case-insensitive)
        const normalizedNote = String(noteText).toLowerCase().trim();
        return !placeholderValues.includes(normalizedNote) && normalizedNote.length > 0;
      })
      .map(([key, item]) => {
        const noteText = item.notes || item.comment;
        // Format key name for display
        const keyName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        return `${keyName}: ${noteText}`;
      })
      .join('; ');

    return {
      isGood: !requiresAttention,
      notes: notes || ''
    };
  };

  // Meter readings.
  //
  // Oct 2026 onwards: rows come from the property's meter register, already
  // joined to type / serial / location by the API. Sep 2026 and earlier: the
  // legacy [name, reading, serial] triples in reading_fields.
  const useNewMeterRegister = usesNewMeters(data);

  const getMeterReadings = () => {
    if (useNewMeterRegister) {
      const rows = Array.isArray(data.meter_readings)
        ? data.meter_readings
        : [];
      return rows.map((row) => ({
        // There is no meter name any more -- the TYPE is the label.
        name: row.meter_type || 'Meter',
        reading: row.reading || '',
        serial: row.serial_number || '',
        location: row.location || '',
      }));
    }

    const readings = [];
    if (data.reading_fields && Array.isArray(data.reading_fields)) {
      data.reading_fields.forEach((reading, index) => {
        const name = data.reading_fields_names?.[index] || `Meter ${index + 1}`;
        const serial = data.reading_fields_serials?.[index] || '';
        readings.push({ name, reading, serial, location: '' });
      });
    }
    return readings;
  };

  const meterReadings = getMeterReadings();

  // Meter photos (image_5..image_14). Only slots that actually hold an image are
  // rendered — MTVH reports created before meter photos were enabled have none,
  // and an all-"No photo" page would just be noise. Labels come from each slot's
  // name field, falling back to the standard defaults.
  const METER_PHOTO_DEFAULT_LABELS = {
    image_5: 'Gas Meter 1',
    image_6: 'Gas Meter 2',
    image_7: 'Electric Meter',
    image_8: 'Water Meter',
  };
  // Meter photos.
  //
  // Oct 2026 onwards: one photo per registered meter, captioned TYPE + SERIAL
  // so two meters of the same type stay distinguishable -- the caption stands
  // alone under the image with nothing else to tell them apart.
  const newMeterPhotos = useNewMeterRegister
    ? (Array.isArray(data.meter_readings) ? data.meter_readings : []).map(
        (row) => ({
          field: 'meter_photo_' + row.property_meter_id,
          url: row.photo_url,
          label: [row.meter_type, row.serial_number]
            .filter(Boolean)
            .join(' \u2014 ')
            .toUpperCase() || 'METER',
        })
      )
    : [];
  const legacyMeterPhotos = [
    'image_5', 'image_6', 'image_7', 'image_8', 'image_9',
    'image_10', 'image_11', 'image_12', 'image_13', 'image_14',
  ]
    .map((field) => ({
      field,
      url: data[`${field}_url`],
      label: (
        data[`${field}_name`] ||
        METER_PHOTO_DEFAULT_LABELS[field] ||
        'Meter'
      ).toUpperCase(),
    }))
    .filter((photo) => photo.url);

  const meterPhotos = useNewMeterRegister
    ? newMeterPhotos.filter((photo) => photo.url)
    : legacyMeterPhotos;

  // Checklist sections
  const checklistItems = [
    { name: 'External', key: 'external' },
    { name: 'Communal area', key: 'location' },
    { name: 'Kitchen', key: 'kitchen' },
    { name: 'Bedroom', key: 'living_space' },
    { name: 'Bathroom', key: 'bathroom' },
    { name: 'General Comments', key: 'other' },
  ];

  return (
    <Document>
      {/* Page 1: Cover Page */}
      <Page size="A4" style={styles.coverPage}>
        <View style={styles.coverPageInnerView}>
          <Image
            src="/assets/images/reports/building_first_page.jpg"
            style={styles.coverBackground}
          />
          
          <Image
            src="/assets/images/GG_Logo_RGB_800.png"
            style={styles.coverLogo}
          />
          
          <View style={styles.coverTitleBox}>
            <Text style={styles.coverTitle}>
              ENSURE YOUR{'\n'}PROPERTY IS SAFE{'\n'}WITH PROPERTY{'\n'}GUARDIANS
            </Text>
          </View>
          
          <View style={styles.coverFooter}>
            <Text style={styles.coverFooterWebsite}>www.global-guardians.co.uk</Text>
            <Text style={styles.coverFooterPhone}>0800 368 9601</Text>
          </View>
        </View>
      </Page>

      {/* Page 2: Report Details and Meters */}
      <Page size="A4" style={styles.contentPage}>
        <View style={styles.logoContainer}>
          <Image
            src="/assets/images/GG_Logo_RGB_800.png"
            style={styles.pageLogo}
          />
        </View>

        <Text style={styles.pageTitle}>
          Monthly Inspection Report For The Month Of {getMonthName(data.month)} {data.year}
        </Text>

        {/* Property Info Table */}
        <View style={styles.infoTable}>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Property Address:</Text>
            <Text style={styles.infoValue}>
              {data.address_1 || data.property_address_1 || ''}{data.address_2 ? `, ${data.address_2}` : ''}{data.town ? `, ${data.town}` : ''}{data.postcode ? `, ${data.postcode}` : ''}
            </Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Client Contact:</Text>
            <Text style={styles.infoValue}>{data.client_contact || 'N/A'}</Text>
          </View>
          <View style={styles.infoRowLast}>
            <Text style={styles.infoLabel}>Organisation:</Text>
            <Text style={styles.infoValue}>{data.client || 'Metropolitan Thames Valley (MTVH)'}</Text>
          </View>
        </View>

        {/* Key Number and Inspection Date Table */}
        <View style={styles.infoTableTwoCol}>
          <View style={styles.infoRowTwoCol}>
            <Text style={styles.infoLabelSmall}>Key Number:</Text>
            <Text style={styles.infoValueSmall}>{data.key_number || data.property_id_name || 'N/A'}</Text>
            <Text style={styles.infoLabelSmall}>Inspection Date:</Text>
            <Text style={styles.infoValueSmallLast}>{formatDate(data.inspection_date)}</Text>
          </View>
          <View style={styles.infoRowTwoCol}>
            <Text style={styles.infoLabelSmall}>Number of Guardians:</Text>
            <Text style={styles.infoValueSmall}>{data.guardians_number || 'N/A'}</Text>
            <Text style={styles.infoLabelSmall}></Text>
            <Text style={styles.infoValueSmallLast}></Text>
          </View>
        </View>

        {/* Meters: readings table followed by the meter photo grid, kept together
            and ahead of the photo categories. */}
        <View style={styles.meterTable}>
          {meterReadings.length > 0 ? (
            meterReadings.map((reading, index) => (
              <View
                key={index}
                style={index === meterReadings.length - 1 ? styles.meterRowLast : styles.meterRow}
              >
                <Text style={styles.meterLabel}>Meter Readings{'\n'}({reading.name}):</Text>
                <Text style={styles.meterValue}>{reading.reading || 'N/A'}</Text>
                <Text style={styles.meterSerialLabel}>Serial Number:</Text>
                <Text style={styles.meterSerialValue}>{reading.serial || 'N/A'}</Text>
              </View>
            ))
          ) : (
            <View style={styles.meterRowLast}>
              <Text style={styles.meterLabel}>Meter Readings:</Text>
              <Text style={styles.meterValue}>N/A</Text>
              <Text style={styles.meterSerialLabel}>Serial Number:</Text>
              <Text style={styles.meterSerialValue}>N/A</Text>
            </View>
          )}
        </View>

        {/* Meter Photo Grid - only the slots that actually hold an image */}
        <View style={styles.meterPhotoGrid}>
          {meterPhotos.map((photo, index) => (
            <View
              key={photo.field}
              style={index % 2 === 0 ? styles.meterPhotoBox : styles.meterPhotoBoxRight}
            >
              <Image src={photo.url} style={styles.meterPhotoImage} />
              <View style={styles.meterPhotoLabel}>
                <Text style={styles.meterPhotoLabelText}>{photo.label}</Text>
              </View>
            </View>
          ))}
        </View>

        <Text style={styles.pageFooter}>www.global-guardians.co.uk</Text>
      </Page>

      {/* Page 3: Photo Sections - MTVH has 12 photos (4 categories x 3 photos each) */}
      <Page size="A4" style={styles.contentPage}>
        <View style={styles.logoContainer}>
          <Image
            src="/assets/images/GG_Logo_RGB_800.png"
            style={styles.pageLogo}
          />
        </View>

        {/* Row 1: First photo category (e.g., FRONT) - image_1, image_1_a, image_1_b */}
        <View style={styles.photoSection}>
          <View style={styles.photoRow}>
            <View style={styles.photoLabel}>
              <View style={styles.photoLabelInner}>
                <Text style={styles.photoLabelText}>{(data.image_1_name || 'FRONT').toUpperCase()}</Text>
              </View>
            </View>
            <View style={styles.photoContainer}>
              <View style={styles.photoWrapper}>
                {data.image_1_url ? (
                  <Image src={data.image_1_url} style={styles.photo} />
                ) : (
                  <Text style={styles.noPhotoText}>No photo</Text>
                )}
              </View>
              <View style={styles.photoWrapper}>
                {data.image_1_a_url ? (
                  <Image src={data.image_1_a_url} style={styles.photo} />
                ) : (
                  <Text style={styles.noPhotoText}>No photo</Text>
                )}
              </View>
              <View style={styles.photoWrapperLast}>
                {data.image_1_b_url ? (
                  <Image src={data.image_1_b_url} style={styles.photo} />
                ) : (
                  <Text style={styles.noPhotoText}>No photo</Text>
                )}
              </View>
            </View>
          </View>
        </View>

        {/* Row 2: Second photo category (e.g., BEDROOM) - image_2, image_2_a, image_2_b */}
        <View style={styles.photoSection}>
          <View style={styles.photoRow}>
            <View style={styles.photoLabel}>
              <View style={styles.photoLabelInner}>
                <Text style={styles.photoLabelText}>{(data.image_2_name || 'BEDROOM').toUpperCase()}</Text>
              </View>
            </View>
            <View style={styles.photoContainer}>
              <View style={styles.photoWrapper}>
                {data.image_2_url ? (
                  <Image src={data.image_2_url} style={styles.photo} />
                ) : (
                  <Text style={styles.noPhotoText}>No photo</Text>
                )}
              </View>
              <View style={styles.photoWrapper}>
                {data.image_2_a_url ? (
                  <Image src={data.image_2_a_url} style={styles.photo} />
                ) : (
                  <Text style={styles.noPhotoText}>No photo</Text>
                )}
              </View>
              <View style={styles.photoWrapperLast}>
                {data.image_2_b_url ? (
                  <Image src={data.image_2_b_url} style={styles.photo} />
                ) : (
                  <Text style={styles.noPhotoText}>No photo</Text>
                )}
              </View>
            </View>
          </View>
        </View>

        {/* Row 3: Third photo category (e.g., KITCHEN/BATHROOM) - image_3, image_3_a, image_3_b */}
        <View style={styles.photoSection}>
          <View style={styles.photoRow}>
            <View style={styles.photoLabel}>
              <View style={styles.photoLabelInner}>
                <Text style={styles.photoLabelText}>{(data.image_3_name || 'KITCHEN/BATHROOM').toUpperCase()}</Text>
              </View>
            </View>
            <View style={styles.photoContainer}>
              <View style={styles.photoWrapper}>
                {data.image_3_url ? (
                  <Image src={data.image_3_url} style={styles.photo} />
                ) : (
                  <Text style={styles.noPhotoText}>No photo</Text>
                )}
              </View>
              <View style={styles.photoWrapper}>
                {data.image_3_a_url ? (
                  <Image src={data.image_3_a_url} style={styles.photo} />
                ) : (
                  <Text style={styles.noPhotoText}>No photo</Text>
                )}
              </View>
              <View style={styles.photoWrapperLast}>
                {data.image_3_b_url ? (
                  <Image src={data.image_3_b_url} style={styles.photo} />
                ) : (
                  <Text style={styles.noPhotoText}>No photo</Text>
                )}
              </View>
            </View>
          </View>
        </View>

        <Text style={styles.pageFooter}>www.global-guardians.co.uk</Text>
      </Page>

      {/* Page 4: Fourth photo category (e.g., COMMUNAL AREA) - image_4, image_4_a, image_4_b */}
      <Page size="A4" style={styles.contentPage}>
        <View style={styles.logoContainer}>
          <Image
            src="/assets/images/GG_Logo_RGB_800.png"
            style={styles.pageLogo}
          />
        </View>

        {/* Row 4: Fourth photo category (e.g., COMMUNAL AREA) */}
        <View style={styles.photoSection}>
          <View style={styles.photoRow}>
            <View style={styles.photoLabel}>
              <View style={styles.photoLabelInner}>
                <Text style={styles.photoLabelText}>{(data.image_4_name || 'COMMUNAL AREA').toUpperCase()}</Text>
              </View>
            </View>
            <View style={styles.photoContainer}>
              <View style={styles.photoWrapper}>
                {data.image_4_url ? (
                  <Image src={data.image_4_url} style={styles.photo} />
                ) : (
                  <Text style={styles.noPhotoText}>No photo</Text>
                )}
              </View>
              <View style={styles.photoWrapper}>
                {data.image_4_a_url ? (
                  <Image src={data.image_4_a_url} style={styles.photo} />
                ) : (
                  <Text style={styles.noPhotoText}>No photo</Text>
                )}
              </View>
              <View style={styles.photoWrapperLast}>
                {data.image_4_b_url ? (
                  <Image src={data.image_4_b_url} style={styles.photo} />
                ) : (
                  <Text style={styles.noPhotoText}>No photo</Text>
                )}
              </View>
            </View>
          </View>
        </View>

        <Text style={styles.pageFooter}>www.global-guardians.co.uk</Text>
      </Page>

      {/* Page 5: Monthly Inspection Checklist */}
      <Page size="A4" style={styles.contentPage}>
        <View style={styles.logoContainer}>
          <Image
            src="/assets/images/GG_Logo_RGB_800.png"
            style={styles.pageLogo}
          />
        </View>

        <Text style={styles.checklistTitle}>Monthly Inspection Checklist</Text>

        <View style={styles.checklistTable}>
          {/* Header */}
          <View style={styles.checklistHeader}>
            <View style={[styles.checklistHeaderCell, styles.checklistHeaderName]}>
              <Text>Item Name</Text>
            </View>
            <View style={[styles.checklistHeaderCell, styles.checklistHeaderCondition]}>
              <Text>Condition</Text>
              <View style={styles.conditionSubHeader}>
                <Text style={styles.conditionGood}>Good</Text>
                <Text style={styles.conditionAttention}>Requires{'\n'}Attention</Text>
              </View>
            </View>
            <View style={[styles.checklistHeaderCell, styles.checklistHeaderNotes, { borderRight: 0 }]}>
              <Text>Notes</Text>
            </View>
          </View>

          {/* Checklist Rows */}
          {checklistItems.map((item, index) => {
            const status = getChecklistStatus(item.key);
            const isLast = index === checklistItems.length - 1;
            return (
              <View key={item.key} style={isLast ? styles.checklistRowLast : styles.checklistRow}>
                <Text style={styles.checklistName}>{item.name}</Text>
                <View style={styles.checklistConditionCell}>
                  <View style={styles.checklistGood}>
                    {status.isGood ? (
                      <Svg width="12" height="12" viewBox="0 0 24 24">
                        <Path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="#000" />
                      </Svg>
                    ) : null}
                  </View>
                  <Text style={styles.checklistAttention}>
                    {!status.isGood ? 'X' : ''}
                  </Text>
                </View>
                <Text style={styles.checklistNotes}>{status.notes}</Text>
              </View>
            );
          })}
        </View>

        <Text style={styles.priorityNote}>
          All issues raised in this Monthly Inspection Report will be documented and resolved within Global Guardians' priority scheme.
        </Text>

        <Text style={styles.conclusionTitle}>Conclusion</Text>
        
        <Text style={styles.conclusionText}>
          I trust that this inspection report is satisfactory.
        </Text>
        
        <Text style={styles.conclusionText}>
          If you have any questions, requests or if you would like Global Guardians to provide you with a no obligation free-of-charge quote for any other vacant properties you manage please do not hesitate to contact me and I would be delighted to assist you.
        </Text>

        <Text style={styles.signOff}>Yours sincerely,</Text>

        <View style={styles.inspectorInfo}>
          <Text style={styles.inspectorName}>
            Property Inspector: {data.account_manager || 'Nick Knight'}
          </Text>
          <Text style={styles.inspectorEmail}>
            Email: {data.account_manager_email || 'nick.knight@global-guardians.co.uk'}
          </Text>
        </View>

        <Text style={styles.pageFooter}>www.global-guardians.co.uk</Text>
      </Page>

      {/* Page 6: Footer/Contact Page */}
      <Page size="A4" style={styles.footerPage}>
        <View style={styles.logoContainer}>
          <Image
            src="/assets/images/GG_Logo_RGB_800.png"
            style={styles.pageLogo}
          />
        </View>

        <View style={styles.footerContent}>
          <Text style={styles.footerCompanyName}>Global Guardians</Text>
          <Text style={styles.footerAddress}>Sopers House</Text>
          <Text style={styles.footerAddress}>Sopers Road</Text>
          <Text style={styles.footerAddress}>Cuffley</Text>
          <Text style={styles.footerAddress}>Potters Bar</Text>
          <Text style={styles.footerAddress}>EN6 4RY</Text>
          <Text style={styles.footerAddress}>UK</Text>

          <Text style={styles.footerEmail}>
            <Text style={styles.footerEmailLabel}>t: </Text>
            0800 368 9601
          </Text>
          <Text style={styles.footerEmail}>
            <Text style={styles.footerEmailLabel}>e: </Text>
            enquiries@global-guardians.co.uk
          </Text>
          <Text style={styles.footerWebsite}>www.Global-Guardians.co.uk</Text>

          {/* Partner Logos */}
          <View style={styles.footerLogosContainer}>
            <Image
              src="/assets/images/mtvh-pp-logos-pdf/mtvh-pp-logo-pdf-1.png"
              style={styles.footerLogoItem}
            />
            <Image
              src="/assets/images/mtvh-pp-logos-pdf/mtvh-pp-logo-pdf-2.png"
              style={styles.footerLogoItem}
            />
            <Image
              src="/assets/images/mtvh-pp-logos-pdf/mtvh-pp-logo-pdf-3.png"
              style={styles.footerLogoItem}
            />
            <Image
              src="/assets/images/mtvh-pp-logos-pdf/mtvh-pp-logo-pdf-4.png"
              style={styles.footerLogoItem}
            />
            <Image
              src="/assets/images/mtvh-pp-logos-pdf/mtvh-pp-logo-pdf-5.png"
              style={styles.footerLogoItem}
            />
            <Image
              src="/assets/images/mtvh-pp-logos-pdf/mtvh-pp-logo-pdf-6.png"
              style={styles.footerLogoItem}
            />
            <Image
              src="/assets/images/mtvh-pp-logos-pdf/mtvh-pp-logo-pdf-7.png"
              style={styles.footerLogoItem}
            />
            <Image
              src="/assets/images/mtvh-pp-logos-pdf/mtvh-pp-logo-pdf-8.png"
              style={styles.footerLogoItem}
            />
            <Image
              src="/assets/images/mtvh-pp-logos-pdf/mtvh-pp-logo-pdf-9.png"
              style={styles.footerLogoItem}
            />
          </View>
        </View>

        <Text style={styles.pageFooter}>www.global-guardians.co.uk</Text>
      </Page>
    </Document>
  );
};

export default MTVHMonthlyInspectionPDF;

